import React, { useEffect, useState } from "react";
import { api, fmtRelative } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Bot, Play, Pause, Trash2, Sparkles, CalendarClock, MessagesSquare, Eye,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "bg-[#e0f2fe] text-[#0369a1]",
  active: "bg-[#dcfce7] text-[#15803d]",
  paused: "bg-[#fef3c7] text-[#92400e]",
};

function planSummary(plan) {
  if (!plan) return "";
  const s = plan.schedule || {};
  const q = plan.query || {};
  const when =
    s.cadence === "daily" ? `daily at ${s.hour}:00`
      : s.cadence === "monthly" ? `monthly on day ${s.day} at ${s.hour}:00`
        : `every ${s.day} at ${s.hour}:00`;
  const what = q.source === "ledger" ? "realized savings by team" : `top ${q.limit} opportunities`;
  const filters = [];
  if (q.filters?.severity) filters.push(`severity ≥ ${q.filters.severity}`);
  if (q.filters?.min_savings_usd > 0) filters.push(`≥ $${q.filters.min_savings_usd}/mo`);
  if (q.filters?.older_than_days > 0) filters.push(`open > ${q.filters.older_than_days}d`);
  return `Runs ${when} · pulls ${what}${filters.length ? ` (${filters.join(", ")})` : ""} · delivers a ${plan.action?.type === "slack_digest" ? "Slack digest" : "digest"}`;
}

export default function CoWorkers() {
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [draft, setDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => {
    api.coworkers.list().then(setItems).catch(console.error);
  };

  useEffect(() => {
    api.coworkers.templates().then(setTemplates).catch(console.error);
    load();
  }, []);

  const generate = async () => {
    if (!description.trim()) {
      toast.error("Describe the task first");
      return;
    }
    setGenerating(true);
    try {
      const cw = await api.coworkers.create(description, name || undefined);
      setDraft(cw);
      toast.success("Plan compiled", {
        description: cw.plan_source === "llm" ? "Compiled by Claude — review & activate." : "Compiled by the heuristic engine — review & activate.",
      });
      load();
    } catch (e) {
      toast.error("Compile failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setGenerating(false);
    }
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error("Action failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const runNow = async (cw) => {
    try {
      const res = await api.coworkers.runNow(cw.id);
      toast.success(`Ran "${cw.name}"`, {
        description: res.delivered_via === "slack" ? "Digest delivered to Slack." : `${res.items} items — digest stored as preview (no Slack webhook configured).`,
      });
      load();
    } catch (e) {
      toast.error("Run failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="coworkers-page">
      {/* Builder */}
      <div className="cs-card p-6" data-testid="coworker-builder">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[#00579e]" />
          <h2 className="cs-heading text-base font-semibold">Hire a Co-Worker</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Describe a recurring FinOps task in plain English. ASTRA compiles it into an auditable plan you can review before activating.
        </p>

        <div className="flex flex-wrap gap-2 mb-3">
          {templates.map((t) => (
            <button
              key={t.id}
              data-testid={`template-${t.id}`}
              onClick={() => { setDescription(t.description); setName(t.name); }}
              className="px-3 py-1.5 rounded-full border border-border text-xs hover:border-[#00579e] hover:text-[#00579e] transition-colors duration-200"
            >
              {t.name}
            </button>
          ))}
        </div>

        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder='e.g., "Every Monday at 9am, send the top 10 savings opportunities to the FinOps channel"'
          data-testid="coworker-description"
          className="rounded-sm"
          rows={2}
        />
        <div className="flex flex-wrap gap-2 mt-3 items-center">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Co-worker name (optional)"
            data-testid="coworker-name"
            className="rounded-sm h-9 text-xs max-w-[260px]"
          />
          <Button onClick={generate} disabled={generating} data-testid="generate-plan-button"
            className="rounded-full h-9 bg-[#00579e] hover:bg-[#004580] text-white text-xs px-5">
            <Bot className="w-3.5 h-3.5 mr-1.5" /> {generating ? "Compiling plan…" : "Compile Plan"}
          </Button>
        </div>

        {draft && (
          <div className="mt-4 border border-[#00579e]/30 bg-[hsl(var(--cs-tint))] rounded-sm p-4" data-testid="draft-plan-preview">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[#00579e]" />
              <div className="text-sm font-semibold">{draft.name}</div>
              <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${STATUS_STYLES[draft.status]}`}>{draft.status}</span>
              <span className="text-[10px] text-muted-foreground cs-mono uppercase ml-auto">plan: {draft.plan_source}</span>
            </div>
            <p className="text-xs text-foreground mt-2">{planSummary(draft.plan)}</p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" data-testid="activate-draft-button"
                onClick={() => act(() => api.coworkers.activate(draft.id).then(() => setDraft(null)), "Co-worker activated")}
                className="rounded-full h-8 bg-[#15803d] hover:bg-[#166534] text-white text-xs px-4">
                <Play className="w-3 h-3 mr-1" /> Activate
              </Button>
              <Button size="sm" variant="outline" onClick={() => runNow(draft)} data-testid="test-run-draft"
                className="rounded-full h-8 text-xs px-4">
                <Eye className="w-3 h-3 mr-1" /> Test Run
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Roster */}
      <div className="space-y-3" data-testid="coworker-list">
        <h2 className="cs-heading text-base font-semibold flex items-center gap-2">
          <MessagesSquare className="w-4 h-4 text-[#00579e]" /> Your Co-Workers
          <span className="text-xs text-muted-foreground font-normal">({items.length})</span>
        </h2>
        {items.length === 0 && (
          <div className="cs-card p-10 text-center text-muted-foreground" data-testid="no-coworkers">
            No co-workers yet — describe a task above or start from a template.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((cw) => (
            <div key={cw.id} className="cs-card p-5" data-testid={`coworker-card-${cw.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="cs-heading text-sm font-semibold truncate">{cw.name}</div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{cw.description}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase font-semibold shrink-0 ${STATUS_STYLES[cw.status] || ""}`} data-testid={`coworker-status-${cw.id}`}>
                  {cw.status}
                </span>
              </div>
              <div className="text-[11px] text-foreground bg-[hsl(var(--cs-tint))] border border-border rounded-sm p-2 mt-3">
                {planSummary(cw.plan)}
              </div>
              <div className="text-[10px] text-muted-foreground mt-2">
                {cw.run_count > 0
                  ? <>Ran {cw.run_count}× · last {fmtRelative(cw.last_run_at)} via {cw.last_run_result?.delivered_via}</>
                  : "Never run"}
                {" · "}plan: <span className="cs-mono uppercase">{cw.plan_source}</span>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {cw.status !== "active" ? (
                  <Button size="sm" onClick={() => act(() => api.coworkers.activate(cw.id), "Activated")}
                    data-testid={`activate-${cw.id}`} className="rounded-full h-7 bg-[#15803d] hover:bg-[#166534] text-white text-[11px] px-3">
                    <Play className="w-3 h-3 mr-1" /> Activate
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => act(() => api.coworkers.pause(cw.id), "Paused (kill switch)")}
                    data-testid={`pause-${cw.id}`} className="rounded-full h-7 text-[11px] px-3 border-[#92400e] text-[#92400e] hover:bg-[#92400e] hover:text-white">
                    <Pause className="w-3 h-3 mr-1" /> Pause
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => runNow(cw)} data-testid={`run-now-${cw.id}`}
                  className="rounded-full h-7 text-[11px] px-3">
                  <Play className="w-3 h-3 mr-1" /> Run Now
                </Button>
                {cw.last_run_result && (
                  <Button size="sm" variant="outline" onClick={() => setViewing(cw)} data-testid={`view-digest-${cw.id}`}
                    className="rounded-full h-7 text-[11px] px-3">
                    <Eye className="w-3 h-3 mr-1" /> Last Digest
                  </Button>
                )}
                <button onClick={() => act(() => api.coworkers.remove(cw.id), "Deleted")} data-testid={`delete-${cw.id}`}
                  className="ml-auto text-muted-foreground hover:text-[#b91c1c] transition-colors duration-200">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Digest viewer */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-2xl rounded-sm border-border" data-testid="digest-modal">
          <DialogHeader>
            <DialogTitle className="cs-heading">{viewing?.name} — last digest</DialogTitle>
            <DialogDescription>
              {viewing?.last_run_result?.items} items · delivered via {viewing?.last_run_result?.delivered_via}
              {viewing?.last_run_result?.delivered_via === "preview" && " (configure SLACK_WEBHOOK_URL to deliver to Slack)"}
            </DialogDescription>
          </DialogHeader>
          <pre className="cs-mono text-[12px] bg-[#FAFAFA] border border-border p-4 rounded-sm max-h-96 overflow-y-auto whitespace-pre-wrap leading-relaxed" data-testid="digest-text">
            {viewing?.last_run_result?.digest}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
